'use client'
import { createContext, use, type ReactNode, useMemo, useCallback } from 'react'
import { type Content, type ModalContent } from '@/features/cms/components/CMS/types'
import { trackElementClicked, trackElementViewed } from '@/features/shared/analytics/element'
import { type ElementClicked, type ElementViewed } from '@shipt/analytics-member-web'
import {
  type PromoCardEvent,
  trackPromoCardClicked,
  trackPromoCardViewed,
} from '@/features/shared/analytics/promoCard'
import { useMutationIncrementViewedElementTrackerV2 } from '@/features/shared/services/ViewedElementTracker/mutations'
import { logError } from '@/features/shared/utils/logger'
import { useSetAtom } from 'jotai'
import { dismissedCMSContentAtom } from '@/features/shared/state/DismissedCMSContent/atoms'

type Props = { data: Content; children: ReactNode }

const CMSContext = createContext<Content | null>(null)

export const CMSContextProvider = ({ children, data }: Props) => {
  return <CMSContext value={data}>{children}</CMSContext>
}

export const useCMSContext = () => use(CMSContext)

export const useDismissCMSContent = (data: Content | ModalContent | null) => {
  const dismissAtom = useSetAtom(dismissedCMSContentAtom)
  // this needs to come from CMS metadata
  const isDismissable = !!data?.metadata?.dismissible
  const { mutateAsync: updateViewTracker } = useMutationIncrementViewedElementTrackerV2({
    refetch: false,
  })

  function dismissContent() {
    if (!data?.metadata?.dismissible) return

    const analyticSlug = data.analytic_slug
    if (!analyticSlug) {
      logError('CMS content missing analytic_slug, cannot track dismissal', {
        id: data.id,
      })
      return
    }

    // fire and forget
    updateViewTracker(analyticSlug)
    // call view tracker to tell it to track this dismissal
    // update global state to persist dismissal across page views
    dismissAtom((state) => [...state, analyticSlug])
  }

  return { isDismissable, dismissContent }
}

const useCMSTrackingProperties = () => {
  const data = useCMSContext()

  return useMemo(
    () =>
      data
        ? {
            display_sublocation: data.analytic_slug,
            shelf_name: (data.data && 'heading' in data.data && data.data.heading) || undefined,
            shelf_type: data.content_type_id,
          }
        : {},
    [data],
  )
}

export const useCMSTrackingEvents = () => {
  const cmsProperties = useCMSTrackingProperties()

  const trackCMSElementViewed = useCallback(
    (eventProperties: Omit<ElementViewed['properties'], 'location'>) => {
      trackElementViewed({ ...eventProperties, ...cmsProperties })
    },
    [cmsProperties],
  )

  const trackCMSElementClicked = useCallback(
    (eventProperties: Omit<ElementClicked['properties'], 'location'>) => {
      trackElementClicked({ ...eventProperties, ...cmsProperties }, { sendImmediately: true })
    },
    [cmsProperties],
  )

  const trackCMSPromoCardViewed = useCallback(
    (eventProperties: Omit<PromoCardEvent, 'location'>) => {
      trackPromoCardViewed({ ...eventProperties, ...cmsProperties })
    },
    [cmsProperties],
  )

  const trackCMSPromoCardClicked = useCallback(
    (eventProperties: Omit<PromoCardEvent, 'location'>) => {
      trackPromoCardClicked({ ...eventProperties, ...cmsProperties }, { sendImmediately: true })
    },
    [cmsProperties],
  )

  return {
    trackCMSElementViewed,
    trackCMSElementClicked,
    trackCMSPromoCardClicked,
    trackCMSPromoCardViewed,
  }
}
